// CotIdel — Refonte interface moderne 2026
// Layout 2 colonnes : sélection à gauche, résultats dashboard à droite
// Design épuré, intuitif, dans l'air du temps

import { useState, useCallback, useMemo } from 'react';
import { CARE_CATEGORIES, OPTIONS, PERIODS, BSI_FORFAIT_IDS, BSI_PROMPT_CARE_IDS, DIABETE_ACT_IDS, DIABETE_SURVEILLANCE_HEBDO_ID } from './data/careData';
import { runCotIciAnalysis } from './engineAdapter';
import { analyserBSI, getReglesApplicables } from './intelligence/bsiEngine';
import { genererExplication } from './intelligence/explanationEngine';
import type { ActeSelectionne, BSIResult, RegleAppliquee, MajorationFlags } from './intelligence/types';
import type { ExplicationBSI } from './intelligence/explanationEngine';
import type { AnalyseResult } from '@/pages/analysis/engine';
import CareDrawer from './components/CareDrawer';
import BSIForfaitPrompt from './components/BSIForfaitPrompt';
import SelectionSummary from './components/SelectionSummary';
import OptionsPanel from './components/OptionsPanel';
import DiabeteSurveillanceBanner from './components/DiabeteSurveillanceBanner';
import BSIResultsDisplay from './components/BSIResultsDisplay';
import ResultsDisplay from './components/ResultsDisplay';

export default function CotIciPage() {
  // ═══ ÉTATS ═══
  const [matinItems, setMatinItems] = useState<string[]>([]);
  const [midiItems, setMidiItems] = useState<string[]>([]);
  const [soirItems, setSoirItems] = useState<string[]>([]);
  const [options, setOptions] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<AnalyseResult | null>(null);
  const [bsiResult, setBsiResult] = useState<BSIResult | null>(null);
  const [bsiExplication, setBsiExplication] = useState<ExplicationBSI | null>(null);
  const [bsiRegles, setBsiRegles] = useState<RegleAppliquee[]>([]);
  const [mode, setMode] = useState<'coter' | 'expliquer'>('coter');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [buttonFlash, setButtonFlash] = useState<'coter' | 'expliquer' | null>(null);
  const [showBsiPrompt, setShowBsiPrompt] = useState(false);
  const [pendingBsiPeriod, setPendingBsiPeriod] = useState<'matin' | 'midi' | 'soir'>('matin');

  // ═══ TOGGLES ═══
  const handleToggle = useCallback((period: 'matin' | 'midi' | 'soir', itemId: string) => {
    const setter = period === 'matin' ? setMatinItems : period === 'midi' ? setMidiItems : setSoirItems;
    const current = period === 'matin' ? matinItems : period === 'midi' ? midiItems : soirItems;
    const isTurningOn = !current.includes(itemId);
    const hasForfait = BSI_FORFAIT_IDS.some((fid) =>
      matinItems.includes(fid) || midiItems.includes(fid) || soirItems.includes(fid)
    );

    setter((prev) => prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]);

    if (isTurningOn && BSI_PROMPT_CARE_IDS.includes(itemId) && !hasForfait) {
      setPendingBsiPeriod(period);
      setShowBsiPrompt(true);
    }
  }, [matinItems, midiItems, soirItems]);

  const handleSelectBsiForfait = useCallback((forfaitId: string) => {
    const setter = pendingBsiPeriod === 'matin' ? setMatinItems : pendingBsiPeriod === 'midi' ? setMidiItems : setSoirItems;
    setter((prev) => prev.includes(forfaitId) ? prev : [...prev, forfaitId]);
    setShowBsiPrompt(false);
  }, [pendingBsiPeriod]);

  const handleToggleOption = useCallback((optionId: string) => {
    setOptions((prev) => ({ ...prev, [optionId]: !prev[optionId] }));
  }, []);

  const handleAddSurveillanceHebdo = useCallback(() => {
    setMatinItems((prev) => prev.includes(DIABETE_SURVEILLANCE_HEBDO_ID) ? prev : [...prev, DIABETE_SURVEILLANCE_HEBDO_ID]);
  }, []);

  const selectedByPeriod = useMemo(() => ({
    matin: matinItems,
    midi: midiItems,
    soir: soirItems,
  }), [matinItems, midiItems, soirItems]);

  const totalSoins = matinItems.length + midiItems.length + soirItems.length;

  const bsiActif = useMemo(() => {
    const bsiIds = ['bsi-bsa', 'bsi-bsb', 'bsi-bsc'];
    const allItems = [...matinItems, ...midiItems, ...soirItems];
    return allItems.some((id) => bsiIds.includes(id));
  }, [matinItems, midiItems, soirItems]);

  // ═══ CONSTRUCTION ACTES ═══
  const buildActesSelectionnes = useCallback((): ActeSelectionne[] => {
    const allCareItems = CARE_CATEGORIES.flatMap((c) => c.items);
    const itemMap = new Map(allCareItems.map((item) => [item.id, item]));
    const result: ActeSelectionne[] = [];

    for (const id of matinItems) {
      const acte = itemMap.get(id);
      if (acte && !acte.isHeader) result.push({ acte: { id: acte.id, label: acte.label, keyword: acte.keyword, categorie: '' }, periode: 'matin' });
    }
    for (const id of midiItems) {
      const acte = itemMap.get(id);
      if (acte && !acte.isHeader) result.push({ acte: { id: acte.id, label: acte.label, keyword: acte.keyword, categorie: '' }, periode: 'midi' });
    }
    for (const id of soirItems) {
      const acte = itemMap.get(id);
      if (acte && !acte.isHeader) result.push({ acte: { id: acte.id, label: acte.label, keyword: acte.keyword, categorie: '' }, periode: 'soir' });
    }
    return result;
  }, [matinItems, midiItems, soirItems]);

  // ═══ HANDLERS COTER / EXPLIQUER ═══
  const runAnalysis = useCallback((targetMode: 'coter' | 'expliquer') => {
    setLoading(true);
    setMode(targetMode);
    setShowResults(true);
    setButtonFlash(targetMode);
    setTimeout(() => setButtonFlash(null), 700);

    setTimeout(() => {
      if (bsiActif) {
        const actes = buildActesSelectionnes();
        const majFlags: MajorationFlags = {
          nuit: !!options['opt-nuit'],
          nuitProfonde: !!options['opt-nuit-pro'],
          dimanche: !!options['opt-dimanche'],
          jourFerie: !!options['opt-ferie'],
          enfant: !!options['opt-mie'],
        };
        const bsiAnalysis = analyserBSI(actes, majFlags);
        const explication = genererExplication(bsiAnalysis);
        const regles = getReglesApplicables(bsiAnalysis);

        setBsiResult(bsiAnalysis);
        setBsiExplication(explication);
        setBsiRegles(regles);
        setResult(null);
      } else {
        const mk = (ids: string[]) => ids.map((id) => {
          const found = CARE_CATEGORIES.flatMap((c) => c.items).find((item) => item.id === id);
          return found?.keyword || id;
        });

        const activeOptionIds = OPTIONS.filter((o) => options[o.id]).map((o) => o.id);
        const analysisResult = runCotIciAnalysis({
          periods: { matin: mk(matinItems), midi: mk(midiItems), soir: mk(soirItems) },
          optionIds: activeOptionIds,
          bsi: { bsiType: null },
        });
        setResult(analysisResult);
        setBsiResult(null);
        setBsiExplication(null);
      }
      setLoading(false);
      // Sur mobile, défile automatiquement vers les résultats une fois l'analyse terminée
      setTimeout(() => {
        if (window.innerWidth < 1024) {
          document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 80);
    }, 400);
  }, [matinItems, midiItems, soirItems, options, bsiActif, buildActesSelectionnes]);

  const handleCoter = useCallback(() => runAnalysis('coter'), [runAnalysis]);
  const handleExpliquer = useCallback(() => runAnalysis('expliquer'), [runAnalysis]);

  // ═══ HELPERS VISUELS ═══
  const injectionCategoryIds = CARE_CATEGORIES
    .filter((c) => c.id === 'injections' || c.id === 'diabete')
    .flatMap((c) => c.items.map((item) => item.id));
  const hasInjectionSelected = [...matinItems, ...midiItems, ...soirItems]
    .some((id) => injectionCategoryIds.includes(id));
  const immunodeprimeActive = !!options['opt-immunodeprime'];

  // ═══ PROPOSITION SÉANCE HEBDOMADAIRE DIABÈTE (>75 ans) ═══
  const allSelectedIds = [...matinItems, ...midiItems, ...soirItems];
  const hasDiabeteSelected = allSelectedIds.some((id) => DIABETE_ACT_IDS.includes(id));
  const hebdoSelected = allSelectedIds.includes(DIABETE_SURVEILLANCE_HEBDO_ID);

  // ═══ RÉSUMÉ DES SOINS SÉLECTIONNÉS (pour la zone droite) ═══
  const allSelectedPills = useMemo(() => {
    const allItems = CARE_CATEGORIES.flatMap((c) => c.items).filter((i) => !i.isHeader);
    const pills: { id: string; label: string; period: string; periodColor: string }[] = [];

    const periodColors: Record<string, string> = {
      matin: 'bg-amber-100 text-amber-700 border-amber-200',
      midi: 'bg-orange-100 text-orange-700 border-orange-200',
      soir: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };

    for (const id of matinItems) {
      const found = allItems.find((i) => i.id === id);
      if (found) pills.push({ id, label: found.label, period: 'Matin', periodColor: periodColors.matin });
    }
    for (const id of midiItems) {
      const found = allItems.find((i) => i.id === id);
      if (found) pills.push({ id, label: found.label, period: 'Midi', periodColor: periodColors.midi });
    }
    for (const id of soirItems) {
      const found = allItems.find((i) => i.id === id);
      if (found) pills.push({ id, label: found.label, period: 'Soir', periodColor: periodColors.soir });
    }
    return pills;
  }, [matinItems, midiItems, soirItems]);

  // ═══ RENDU ═══
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* ═══ HEADER STICKY ═══ */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center">
              <i className="ri-shield-check-line text-white text-lg"></i>
            </div>
            <div>
              <span className="text-lg font-bold font-heading text-foreground-900 leading-tight">CotIdel</span>
              <p className="text-[10px] text-foreground-400 leading-none -mt-0.5">Assistant NGAP 2026</p>
            </div>
          </div>

          {/* Search bar */}
          <div className="flex-1 max-w-md mx-4 hidden sm:block">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un soin, une catégorie..."
                className="w-full pl-9 pr-9 py-2 text-sm bg-background-100 border border-background-200/60 rounded-xl text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-all"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer">
                  <i className="ri-close-circle-fill text-foreground-300 text-sm hover:text-foreground-500"></i>
                </button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {bsiActif && (
              <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 bg-primary-50 px-3 py-1.5 rounded-full border border-primary-200/50">
                <i className="ri-brain-line text-primary-500"></i>
                BSI actif
              </span>
            )}
            {totalSoins > 0 && (
              <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-600 bg-background-100 px-3 py-1.5 rounded-full border border-background-200/50">
                <i className="ri-stethoscope-line text-foreground-400"></i>
                {totalSoins} soin{totalSoins > 1 ? 's' : ''}
              </span>
            )}
            <button
              onClick={() => {
                setMatinItems([]); setMidiItems([]); setSoirItems([]);
                setOptions({}); setResult(null); setBsiResult(null);
                setBsiExplication(null); setShowResults(false);
                setShowBsiPrompt(false);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              <span className="hidden sm:inline">Réinitialiser</span>
            </button>
          </div>
        </div>
      </header>

      {/* ═══ MAIN 2 COLONNES ═══ */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-5">
        <div className="flex flex-col lg:flex-row gap-5 h-full">

          {/* ═══ COLONNE GAUCHE : SÉLECTION ═══ */}
          <div className="lg:w-[420px] xl:w-[460px] flex-shrink-0 flex flex-col gap-4">

            {/* Récapitulatif sticky */}
            <div className="sticky top-[4.5rem] z-30">
              <SelectionSummary
                selectedByPeriod={selectedByPeriod}
                onToggleItem={handleToggle}
              />
            </div>

            {/* Bandeau BSI */}
            {bsiActif && (
              <div className="bg-primary-50 border border-primary-200/60 rounded-xl px-4 py-3 flex items-start gap-3 animate-fadeIn">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-brain-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-primary-800">Intelligence BSI activée</p>
                  <p className="text-[11px] text-primary-600 leading-relaxed mt-0.5">
                    Forfait, cumuls, AMX, exceptions, article 11B — tout est automatique.
                  </p>
                </div>
              </div>
            )}

            {/* Tiroirs Matin / Midi / Soir */}
            {PERIODS.map((period) => (
              <CareDrawer
                key={period.id}
                period={period}
                selectedItems={selectedByPeriod[period.id]}
                onToggleItem={(id) => handleToggle(period.id, id)}
                searchQuery={searchQuery}
                defaultOpen={period.id === 'matin'}
              />
            ))}

            {/* Options compactes */}
            <div className="bg-white rounded-xl border border-background-200/60 p-4">
              <OptionsPanel options={options} onToggleOption={handleToggleOption} bsiType={null} onBSITypeChange={() => {}} />
            </div>

            {/* Proposition séance hebdo. diabète (>75 ans) */}
            {hasDiabeteSelected && !hebdoSelected && (
              <DiabeteSurveillanceBanner onAdd={handleAddSurveillanceHebdo} />
            )}

            {/* Bandeau immunodéprimé */}
            {hasInjectionSelected && (
              <div className={`rounded-xl border px-4 py-3 transition-all ${
                immunodeprimeActive
                  ? 'bg-accent-50 border-accent-200'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${immunodeprimeActive ? 'bg-accent-100' : 'bg-amber-100'}`}>
                    <i className={`ri-shield-flash-line text-sm ${immunodeprimeActive ? 'text-accent-600' : 'text-amber-600'}`}></i>
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-bold ${immunodeprimeActive ? 'text-accent-800' : 'text-amber-800'}`}>
                      {immunodeprimeActive ? 'Patient immunodéprimé — AMI 1,5' : 'Patient immunodéprimé ?'}
                    </p>
                    <p className={`text-[10px] mt-0.5 ${immunodeprimeActive ? 'text-accent-600' : 'text-amber-700'}`}>
                      {immunodeprimeActive
                        ? 'Les injections sont cotées en AMI 1,5.'
                        : 'Si immunodéprimé, les injections passent de AMI 1 à AMI 1,5.'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleOption('opt-immunodeprime')}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                      immunodeprimeActive
                        ? 'bg-accent-500 text-white hover:bg-accent-600'
                        : 'bg-amber-500 text-white hover:bg-amber-600'
                    }`}
                  >
                    {immunodeprimeActive ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </div>
            )}

            {/* Boutons d'action — barre collante en bas sur mobile */}
            <div className="sticky bottom-0 z-20 lg:static flex gap-3 bg-background-50/95 backdrop-blur-md py-3 lg:py-0 lg:bg-transparent lg:backdrop-blur-none">
              <button
                onClick={handleCoter}
                disabled={loading || totalSoins === 0}
                className={`flex-1 py-3.5 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  loading || totalSoins === 0
                    ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                    : 'bg-primary-500 text-white hover:bg-primary-600 shadow-sm hover:shadow active:scale-[0.98]'
                } ${buttonFlash === 'coter' ? 'animate-button-flash' : ''}`}
              >
                {loading && mode === 'coter' ? (
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                ) : (
                  <i className="ri-calculator-line text-lg"></i>
                )}
                COTER
              </button>
              <button
                onClick={handleExpliquer}
                disabled={loading || totalSoins === 0}
                className={`flex-1 py-3.5 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  loading || totalSoins === 0
                    ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                    : 'bg-accent-500 text-white hover:bg-accent-600 shadow-sm hover:shadow active:scale-[0.98]'
                } ${buttonFlash === 'expliquer' ? 'animate-button-flash' : ''}`}
              >
                {loading && mode === 'expliquer' ? (
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                ) : (
                  <i className="ri-brain-line text-lg"></i>
                )}
                EXPLIQUER
              </button>
            </div>

          </div>

          {/* ═══ COLONNE DROITE : RÉSULTATS ═══ */}
          <div className="flex-1 min-w-0">
            <div className="lg:sticky lg:top-20">
              {/* État vide */}
              {!showResults && !loading && (
                <div className="bg-white rounded-xl border border-background-200/60 p-8 flex flex-col items-center justify-center min-h-[400px] text-center transition-all duration-300">
                  <div className="w-20 h-20 rounded-2xl bg-primary-50 flex items-center justify-center mb-5">
                    <i className="ri-calculator-line text-primary-400 text-3xl"></i>
                  </div>
                  <h2 className="text-xl font-bold font-heading text-foreground-800 mb-2">
                    Prêt à coter ?
                  </h2>
                  <p className="text-sm text-foreground-400 max-w-sm leading-relaxed mb-6">
                    Sélectionnez vos soins dans le panneau de gauche, choisissez les options puis cliquez sur <strong className="text-foreground-600">COTER</strong> ou <strong className="text-foreground-600">EXPLIQUER</strong>.
                  </p>

                  {/* Mini aperçu des soins sélectionnés */}
                  {allSelectedPills.length > 0 && (
                    <div className="w-full max-w-md">
                      <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2.5">
                        Soins sélectionnés
                      </p>
                      <div className="flex flex-wrap gap-1.5 justify-center">
                        {allSelectedPills.map((pill) => (
                          <span
                            key={pill.id}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${pill.periodColor}`}
                          >
                            <span className="w-1 h-1 rounded-full bg-current opacity-50"></span>
                            {pill.label}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-foreground-400 mt-4">
                        {totalSoins} soin{totalSoins > 1 ? 's' : ''} — cliquez sur COTER pour voir le résultat
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Loading state — transition fluide */}
              {loading && (
                <div className="animate-result-enter bg-white rounded-xl border border-background-200/60 p-12 flex flex-col items-center justify-center text-center">
                  <div className="relative w-16 h-16 mb-5">
                    <div className="absolute inset-0 rounded-full border-4 border-primary-100"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary-500 animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className="ri-brain-line text-primary-500 text-xl"></i>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-foreground-600">
                    Analyse en cours...
                  </p>
                  <p className="text-xs text-foreground-400 mt-1.5">
                    L&apos;intelligence CotIdel vérifie les règles NGAP
                  </p>
                  <div className="flex gap-1 mt-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-300 animate-pulse" style={{ animationDelay: '0s' }}></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" style={{ animationDelay: '0.15s' }}></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                  </div>
                </div>
              )}

              {/* Résultats */}
              {!loading && showResults && (
                <div id="results-section" className="animate-result-enter space-y-5 scroll-mt-20">
                  {/* Aperçu rapide sélections — stagger in */}
                  {allSelectedPills.length > 0 && (
                    <div className="animate-stagger-child animate-stagger-1 bg-white rounded-xl border border-background-200/60 p-4">
                      <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2.5">
                        Soins analysés
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {allSelectedPills.map((pill) => (
                          <span
                            key={pill.id}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${pill.periodColor}`}
                          >
                            {pill.period}
                            <span className="w-px h-3 bg-current opacity-20"></span>
                            {pill.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {bsiResult && bsiExplication && (
                    <BSIResultsDisplay
                      bsiResult={bsiResult}
                      explication={bsiExplication}
                      regles={bsiRegles}
                      mode={mode}
                    />
                  )}
                  {result && !bsiResult && (
                    <ResultsDisplay result={result} mode={mode} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-background-200/50 py-5 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[11px] text-foreground-300">
            CotIdel — Assistant de cotation NGAP 2026. Aucune donnée conservée.
          </p>
        </div>
      </footer>

      {/* ═══ POPUP CHOIX DU FORFAIT BSI ═══ */}
      <BSIForfaitPrompt
        open={showBsiPrompt}
        onSelect={handleSelectBsiForfait}
        onClose={() => setShowBsiPrompt(false)}
      />
    </div>
  );
}